import { useMemo } from 'react'
import type { ColumnDef } from '@tanstack/react-table'
import { Check, RefreshCw } from 'lucide-react'
import { Link } from 'react-router'
import type {
  CompletenessResponse,
  Garment,
  GarmentStatus,
  RackItem,
  RackItemStatus,
} from '@fitting-room/contracts'
import { DataTable } from '../../components/DataTable'
import { GownThumb } from '../../components/admin/GownThumb'
import { HelpButton, StorefrontHelp } from '../../components/admin/HelpDrawer'
import { PageHeader } from '../../components/admin/PageHeader'
import {
  useCatalogue,
  useCompleteness,
  useRack,
  useSetGarmentStatus,
  useSetRackItemStatus,
} from '../../lib/adminQueries'
import { useStaffToken } from '../../lib/staffToken'
import { WordsSection } from './words/WordsSection'
import { cardImage } from '../../lib/media'
import { text } from '../../lib/text'
import { BOUTIQUE_LOCALE } from '../../lib/boutique'

const percent = (ratio: number) => `${Math.round(ratio * 100)}%`

/**
 * Spelled out here rather than imported from the contract's zod enum: the
 * customer client shares this bundle, and a value import from the contracts
 * barrel drags every schema in it onto a bride's phone. The TYPE is imported,
 * so a status the boutique's data model gains and this list has not is a
 * typecheck failure rather than a dropdown that quietly lost an option.
 */
const GARMENT_STATUSES: readonly GarmentStatus[] = ['active', 'discontinued', 'draft']
const RACK_STATUSES: readonly RackItemStatus[] = ['available', 'held', 'sold', 'withdrawn']

function Bar({ ratio }: { readonly ratio: number }) {
  return (
    <div className="st-meter">
      <div style={{ width: `${Math.max(0, Math.min(100, ratio * 100))}%` }} />
    </div>
  )
}

/**
 * One style's status, changed by hand.
 *
 * This is what makes the red rows on the Sync gowns screen safe to look at
 * rather than agonize over: the sync discontinues everything Airtable no
 * longer lists, and a stylist who disagrees with one of those rows puts it
 * back here, in the time it takes to notice. Without it her only move is to
 * correct Airtable and wait for tomorrow, with a bride booked at four.
 *
 * A select rather than a button per state, because these are three values of
 * one field and not three actions — and it saves nothing so nothing is lost
 * if she opens it and changes her mind.
 */
function GarmentStatusSelect({ garment }: { readonly garment: Garment }) {
  const { token } = useStaffToken()
  const mutation = useSetGarmentStatus(token, garment.id)
  return (
    <span className="flex items-center gap-2">
      <select
        className="st-select"
        aria-label={`Status of ${text(garment.name, 'en')}`}
        value={garment.status}
        disabled={mutation.isPending}
        onChange={(event) => mutation.mutate({ status: event.target.value as GarmentStatus })}
      >
        {GARMENT_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
      {/* The API's own words. A refusal here is nearly always a token that has
          gone stale on a tablet nobody has signed out of. */}
      {mutation.isError ? <span className="st-badge st-badge--alert">{mutation.error.code}</span> : null}
    </span>
  )
}

function RackStatusSelect({ item }: { readonly item: RackItem }) {
  const { token } = useStaffToken()
  const mutation = useSetRackItemStatus(token, item.id)
  return (
    <span className="flex items-center gap-2">
      <select
        className="st-select"
        aria-label={`Status of piece ${item.externalRef}`}
        value={item.status}
        disabled={mutation.isPending}
        onChange={(event) => mutation.mutate({ status: event.target.value as RackItemStatus })}
      >
        {RACK_STATUSES.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>
      {mutation.isError ? <span className="st-badge st-badge--alert">{mutation.error.code}</span> : null}
    </span>
  )
}

/**
 * The health metric the owner actually needs before switching this on: how
 * much of the catalogue this flow can even score. A boutique with a third of
 * its styles missing the attributes the flow ranks on will get a ranking that
 * looks arbitrary, and that is a data problem, not a tuning problem.
 *
 * The rack is on the same page and in its own table. It is a second catalog
 * with its own key and its own sweep, and a stylist looking for a gown that
 * has vanished from the deck does not know which of the two lists it was in —
 * so both are where she is already looking, and neither is joined to the other.
 */
export function CataloguePage() {
  const { token, role } = useStaffToken()
  const completeness = useCompleteness(token)
  const catalogue = useCatalogue(token)
  const rack = useRack(token)

  const report: CompletenessResponse | undefined = completeness.data
  const locale = BOUTIQUE_LOCALE

  const sampleCounts = useMemo(() => {
    const counts = new Map<string, number>()
    for (const sample of catalogue.data?.samples ?? []) {
      counts.set(sample.garmentId, (counts.get(sample.garmentId) ?? 0) + 1)
    }
    return counts
  }, [catalogue.data])

  const columns = useMemo<ColumnDef<Garment, unknown>[]>(() => {
    const base: ColumnDef<Garment, unknown>[] = [
      // The photo first: a stylist knows a gown by sight before by number.
      {
        id: 'photo',
        header: '',
        enableSorting: false,
        accessorFn: (row) => row.media.length,
        cell: (info) => {
          const photo = cardImage(info.row.original.media)
          return <GownThumb src={photo?.src} srcSet={photo?.srcSet} />
        },
      },
      // Led by the style #, as the rack table below is and as the back office
      // titles a gown everywhere: it is what a stylist finds it by on the rail
      // and writes on the order. The name follows, for the one without a number.
      {
        id: 'code',
        header: 'Style',
        accessorFn: (row) => row.code ?? text(row.name, locale),
        cell: (info) => <span className="font-medium tabular-nums">{String(info.getValue())}</span>,
      },
      {
        id: 'name',
        header: 'Name',
        accessorFn: (row) => text(row.name, locale),
        cell: (info) => <span className="st-meta">{String(info.getValue())}</span>,
      },
      { id: 'collection', header: 'Collection', accessorFn: (row) => row.collection ?? '—' },
      {
        id: 'status',
        header: 'Status',
        // The accessor stays the plain value so the filter box and the sort
        // still work on it; only what is drawn changes.
        accessorFn: (row) => row.status,
        cell: (info) => <GarmentStatusSelect garment={info.row.original} />,
      },
      {
        id: 'samples',
        header: 'Samples',
        accessorFn: (row) => sampleCounts.get(row.id) ?? 0,
        cell: (info) => {
          const count = Number(info.getValue())
          return count === 0 ? (
            <span className="st-badge st-badge--alert">none</span>
          ) : (
            <span className="tabular-nums">{count}</span>
          )
        },
      },
    ]

    // One column per attribute the flow scores on — the columns are the
    // tenant's own attribute list, arriving with the report.
    for (const attribute of report?.perAttribute ?? []) {
      base.push({
        id: `attr-${attribute.key}`,
        header: attribute.label,
        accessorFn: (row) => (row.attributes.some((a) => a.key === attribute.key) ? 1 : 0),
        cell: (info) =>
          Number(info.getValue()) === 1 ? (
            <Check size={14} aria-label="present" style={{ color: 'var(--st-gold-deep)' }} />
          ) : (
            <span className="st-meta">missing</span>
          ),
      })
    }

    return base
  }, [report, sampleCounts, locale])

  const railColumns = useMemo<ColumnDef<RackItem, unknown>[]>(
    () => [
      {
        id: 'photo',
        header: '',
        enableSorting: false,
        accessorFn: (row) => row.media.length,
        cell: (info) => {
          const photo = cardImage(info.row.original.media)
          return <GownThumb src={photo?.src} srcSet={photo?.srcSet} />
        },
      },
      { id: 'barcode', header: 'Barcode', accessorFn: (row) => row.externalRef },
      {
        id: 'style',
        header: 'Style',
        accessorFn: (row) => row.styleCode ?? text(row.name, locale),
        cell: (info) => <span className="font-medium">{String(info.getValue())}</span>,
      },
      { id: 'size', header: 'Size', accessorFn: (row) => row.size },
      { id: 'colour', header: 'Color', accessorFn: (row) => row.colour ?? '—' },
      {
        id: 'status',
        header: 'Status',
        accessorFn: (row) => row.status,
        cell: (info) => <RackStatusSelect item={info.row.original} />,
      },
    ],
    [locale],
  )

  return (
    <div>
      <PageHeader eyebrow="What this flow can score" title="Catalog health">
        {/* Sync gowns starts here because it is something done TO this
            catalog — Airtable read and applied onto it — not a page beside
            it. The owner's, by the same `role === 'owner'` as the console's
            tabs; a stylist has no button rather than one that is refused. */}
        {role === 'owner' ? (
          <Link to="/admin/sync" className="st-btn-ghost">
            <RefreshCw size={14} aria-hidden />
            Sync gowns
          </Link>
        ) : null}
        <HelpButton title="From Airtable to the website">
          <StorefrontHelp />
        </HelpButton>
      </PageHeader>

      {completeness.isError ? (
        <p className="st-alert mb-4">Could not load the completeness report.</p>
      ) : null}

      {report ? (
        <div className="mb-6 grid gap-4 md:grid-cols-[minmax(0,18rem)_1fr]">
          <div className="st-card p-5">
            <div className="st-display text-5xl tabular-nums">{percent(report.ratio)}</div>
            <p className="mt-2 text-sm">
              of your catalog is fully scoreable by this flow ({report.fullyScoreable} of {report.totalGarments} styles).
            </p>
            <p className="st-meta mt-3">
              Scored on: {report.scoredAttributeKeys.length} attribute
              {report.scoredAttributeKeys.length === 1 ? '' : 's'}.
            </p>
          </div>

          <div className="st-card p-5">
            <ul className="space-y-3">
              {report.perAttribute.map((attribute) => (
                <li key={attribute.key}>
                  <div className="mb-1 flex items-baseline justify-between text-sm">
                    <span className="font-medium">{attribute.label}</span>
                    <span className="st-meta tabular-nums">
                      {percent(attribute.ratio)} · {attribute.missing} missing
                    </span>
                  </div>
                  <Bar ratio={attribute.ratio} />
                </li>
              ))}
            </ul>
          </div>
        </div>
      ) : null}

      <h2 className="st-eyebrow mb-2">Styles</h2>
      <p className="st-meta mb-2 max-w-2xl">
        Status is yours to set. The next sync will still discontinue anything Airtable no longer lists, so a
        style you put back here belongs upstream too.
      </p>
      <DataTable
        data={catalogue.data?.garments ?? []}
        columns={columns}
        filterPlaceholder="Filter styles"
        emptyMessage={catalogue.isPending ? 'Loading…' : 'No styles ingested yet.'}
      />

      <h2 className="st-eyebrow mb-2 mt-8">Off the rack</h2>
      <p className="st-meta mb-2 max-w-2xl">
        Physical pieces, one row per gown on the floor. A second catalog: nothing here is joined to a style
        above, even where the numbers match.
      </p>
      <DataTable
        data={rack.data?.items ?? []}
        columns={railColumns}
        filterPlaceholder="Filter off-the-rack pieces"
        emptyMessage={rack.isPending ? 'Loading…' : 'Nothing off the rack.'}
      />

      {/* The sync's vocabulary, so the sync's owner: absent for a stylist,
          like the Sync tab, rather than drawn and refused. */}
      {role === 'owner' ? <WordsSection staffToken={token} /> : null}
    </div>
  )
}
